/**
 * Un sunet scurt și discret la o notificare nouă — ca medicul, cu fila deschisă
 * dar cu ochii în altă parte, să audă că a apărut ceva.
 *
 * E sintetizat cu Web Audio, nu un fișier: nu adaugă un asset binar în build și
 * sună la fel în orice browser. Contextul audio pornește doar după o interacțiune
 * a utilizatorului — aici e mereu cazul, fiindcă sunetul vine după autentificare —
 * și dacă browserul îl refuză, funcția tace fără să arunce.
 */

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctx: Ctor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!Ctx) return null;
  try {
    if (!ctx) ctx = new Ctx();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Un semnal sonor scurt.
 *
 * `normal` — două note blânde, un „ding-dong", pentru orice notificare.
 * `urgent` — trei note mai insistente și puțin mai tari, ca o urgență (un
 * finding fără plan, semnat) să se distingă de una obișnuită și medicul să știe
 * după sunet, fără să se uite, că merită întrerupt.
 */
export function playChime(kind: 'normal' | 'urgent' = 'normal'): void {
  const ac = audioCtx();
  if (!ac) return;
  try {
    // Un context suspendat (fila a fost în fundal) trebuie reluat întâi.
    if (ac.state === 'suspended') void ac.resume();
    const now = ac.currentTime;
    // Nota, momentul și vârful de volum. Urgentul: trei note ascendente, mai
    // dese și cu un pic mai mult volum.
    const notes =
      kind === 'urgent'
        ? [
            { freq: 987.77, at: 0, peak: 0.2 }, // B5
            { freq: 1318.51, at: 0.11, peak: 0.2 }, // E6
            { freq: 1567.98, at: 0.22, peak: 0.22 }, // G6
          ]
        : [
            { freq: 880, at: 0, peak: 0.14 }, // A5
            { freq: 1174.66, at: 0.14, peak: 0.14 }, // D6
          ];
    const tail = kind === 'urgent' ? 0.26 : 0.32;
    for (const n of notes) {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = 'sine';
      osc.frequency.value = n.freq;
      // Atac scurt, cădere lină — un clopoțel, nu un bip.
      const start = now + n.at;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(n.peak, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + tail);
      osc.connect(gain).connect(ac.destination);
      osc.start(start);
      osc.stop(start + tail + 0.02);
    }
  } catch {
    // Un browser care refuză sunetul nu trebuie să strice restul.
  }
}
